import { useState, useEffect } from "react";
import { useLang } from "@/i18n";
import { Menu, X } from "lucide-react";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = [
    { label: t.nav.prodotti, href: "#prodotti" },
    { label: t.nav.marchi, href: "#marchi" },
    { label: t.nav.esperienza, href: "#esperienza" },
    { label: t.nav.canali, href: "#canali" },
    { label: t.nav.gruppi, href: "#gruppi" },
  ];

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const LangSwitch = ({ testid }) => (
    <div data-testid={testid} className="flex items-center border border-ink/80">
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 text-xs font-bold uppercase tracking-[0.1em] transition-colors duration-200 ${lang === l ? "bg-ferrari text-white" : "bg-white text-ink hover:bg-[#f4f4f4]"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-[border,box-shadow] duration-300 ${
        scrolled ? "border-b border-transparent shadow-[0_10px_30px_-12px_rgba(0,0,0,0.18)]" : "border-b border-transparent shadow-none"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 h-[72px] flex items-center justify-between">
        <button data-testid="logo-btn" onClick={() => go("#top")} className="flex items-center gap-2 group">
          <span className="w-3 h-3 bg-ferrari group-hover:rotate-45 transition-transform duration-300" />
          <span style={{ fontFamily: "'Times New Roman', Times, serif" }} className="text-2xl font-bold tracking-tight">
            <span className="text-ferrari">PST</span> <span className="text-ink">Pellet</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {nav.map((n) => (
            <button
              key={n.href}
              data-testid={`nav-${n.href.slice(1)}`}
              onClick={() => go(n.href)}
              className="text-xs font-semibold uppercase tracking-[0.15em] text-ink/70 hover:text-ferrari transition-colors duration-200"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><LangSwitch testid="lang-switch-desktop" /></div>
          <button
            data-testid="header-quote-btn"
            onClick={() => go("#preventivo")}
            className="hidden md:inline-flex items-center bg-ferrari text-white text-xs font-bold uppercase tracking-[0.15em] px-6 py-3 hover:bg-ink transition-colors duration-200"
          >
            {t.nav.cta}
          </button>
          <button data-testid="menu-toggle" className="lg:hidden p-2" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-ink/80 px-5 py-4 flex flex-col gap-1">
          {nav.map((n) => (
            <button
              key={n.href}
              data-testid={`mobile-nav-${n.href.slice(1)}`}
              onClick={() => go(n.href)}
              className="text-left py-3 text-sm font-semibold uppercase tracking-wide border-b border-border last:border-0"
            >
              {n.label}
            </button>
          ))}
          <div className="flex items-center justify-between mt-3">
            <LangSwitch testid="lang-switch-mobile" />
            <button
              data-testid="mobile-quote-btn"
              onClick={() => go("#preventivo")}
              className="bg-ferrari text-white text-sm font-bold uppercase tracking-wide px-6 py-3"
            >
              {t.nav.cta}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
